import { useState } from 'react'
import { useAtomValue } from 'jotai'
import { Navigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Check, X, Search, Loader2 } from 'lucide-react'
import { UserRoleIndex } from '@/types'

import { userAtom } from '@/store/store'
import useRequests from '@/hooks/requests/useRequests'
import useRequestMutation from '@/hooks/requests/useRequestMutation'
import { checkADGroup, ADGroupCheckResult } from '@/api/requests'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog'

const ManageRequests = () => {
    const user = useAtomValue(userAtom)
    const { data: requests, isLoading } = useRequests()
    const { updateStatus } = useRequestMutation()

    const [adGroupDialogOpen, setAdGroupDialogOpen] = useState(false)
    const [adGroupLoading, setAdGroupLoading] = useState(false)
    const [adGroupResult, setAdGroupResult] = useState<ADGroupCheckResult | null>(null)
    const [adGroupName, setAdGroupName] = useState('')
    const [adGroupError, setAdGroupError] = useState<string | null>(null)

    // Protect the route (double check client-side)
    if (user.userRoleIndex !== UserRoleIndex.Dev) {
        return <Navigate to="/" replace />
    }

    const handleStatusUpdate = (requestId: string, status: 'Approved' | 'Rejected') => {
        updateStatus.mutate({
            request_id: requestId,
            status,
            reviewer_id: user.userId,
        })
    }

    const handleCheckADGroup = async (courseCode: string, section: string, semester: string) => {
        const groupName = `${courseCode}${section}${semester}students`
        setAdGroupName(groupName)
        setAdGroupResult(null)
        setAdGroupError(null)
        setAdGroupLoading(true)
        setAdGroupDialogOpen(true)

        try {
            const result = await checkADGroup(groupName)
            
            // If a group was found, also fetch its members
            if (result.exists && result.group?.id) {
                try {
                    const idToken = (await import('@/auth/utils')).refreshAuthTokenAndUpdateUser
                    const token = await idToken()
                    const API_URL = import.meta.env.VITE_API_URL
                    const membersResponse = await fetch(
                        `${API_URL}/api/requests/ad-group-members/${result.group.id}`,
                        {
                            headers: { Authorization: `Bearer ${token}` },
                        },
                    )
                    if (membersResponse.ok) {
                        const membersData = await membersResponse.json()
                        result.members = membersData.members || []
                    } else {
                        result.members = []
                    }
                } catch {
                    result.members = []
                }
            } else {
                result.members = []
            }

            setAdGroupResult(result)
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Failed to check AD group'
            setAdGroupError(message)
            toast.error(message)
        } finally {
            setAdGroupLoading(false)
        }
    }

    if (isLoading) {
        return <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
        </div>
    }

    return (
        <div className="w-full p-4">
            <h1 className="text-2xl font-bold mb-4">Manage Course Requests</h1>
            <div className="border rounded-md">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead className="text-center">Requested By</TableHead>
                            <TableHead className="text-center">Course Code</TableHead>
                            <TableHead className="text-center">Section</TableHead>
                            <TableHead className="text-center">Semester</TableHead>
                            <TableHead className="text-center">Status</TableHead>
                            <TableHead className="text-center">Date</TableHead>
                            <TableHead className="text-center">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {requests?.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center h-24">
                                    No requests found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            requests?.map((request) => (
                                <TableRow key={request.requestId} className="hover:bg-muted/50 transition-colors">
                                    <TableCell className="text-center">{request.requestedBy}</TableCell>
                                    <TableCell className="text-center">{request.courseCode}</TableCell>
                                    <TableCell className="text-center">{request.section}</TableCell>
                                    <TableCell className="text-center">{request.semester}</TableCell>
                                    <TableCell className="text-center">
                                        <Badge variant={
                                            request.status === 'Approved' ? 'default' :
                                                request.status === 'Rejected' ? 'destructive' : 'secondary'
                                        }>
                                            {request.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-center">{new Date(request.createdAt).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2 whitespace-nowrap">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => handleCheckADGroup(request.courseCode, request.section, request.semester)}
                                                >
                                                    <Search className="h-4 w-4 mr-1" />
                                                    Check AD Group
                                                </Button>
                                        {request.status === 'Pending' && (
                                            <>
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleStatusUpdate(request.requestId, 'Approved')}
                                                    disabled={updateStatus.isPending}
                                                >
                                                    <Check className="h-4 w-4 mr-1" />
                                                    Approve
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleStatusUpdate(request.requestId, 'Rejected')}
                                                    disabled={updateStatus.isPending}
                                                >
                                                    <X className="h-4 w-4 mr-1" />
                                                    Reject
                                                </Button>
                                            </>
                                        )}
                                        {request.reviewer && (
                                            <span className="text-xs text-muted-foreground ml-2">
                                                Reviewed by {request.reviewer}
                                            </span>
                                        )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* AD Group Check Modal */}
            <Dialog open={adGroupDialogOpen} onOpenChange={setAdGroupDialogOpen}>
                <DialogContent className="max-w-xl">
                    <DialogHeader>
                        <DialogTitle>AD Group Check</DialogTitle>
                        <DialogDescription>
                            Checking group: <span className="font-mono font-semibold">{adGroupName}</span>
                        </DialogDescription>
                    </DialogHeader>

                    {adGroupLoading && (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                            <span className="ml-2 text-muted-foreground">Checking AD group...</span>
                        </div>
                    )}

                    {adGroupError && (
                        <div className="py-4 text-center text-destructive">
                            {adGroupError}
                        </div>
                    )}

                    {adGroupResult && !adGroupLoading && (
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <span className="font-medium">Status:</span>
                                {adGroupResult.exists ? (
                                    <Badge variant="default">
                                        <Check className="h-3 w-3 mr-1" />
                                        Group Exists
                                    </Badge>
                                ) : (
                                    <Badge variant="destructive">
                                        <X className="h-3 w-3 mr-1" />
                                        Group Not Found
                                    </Badge>
                                )}
                            </div>

                            {adGroupResult.exists && adGroupResult.group && (
                                <div className="text-sm">
                                    <p><span className="font-medium">Matched Group:</span>{' '}
                                        <span className="font-mono">{adGroupResult.group.displayName}</span>
                                    </p>
                                </div>
                            )}

                            {adGroupResult.exists && adGroupResult.members.length > 0 && (
                                <div>
                                    <h4 className="font-medium mb-2">
                                        Members ({adGroupResult.members.length})
                                    </h4>
                                    <div className="border rounded-md max-h-64 overflow-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Name</TableHead>
                                                    <TableHead>Email</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {adGroupResult.members.map((member, idx) => (
                                                    <TableRow key={idx}>
                                                        <TableCell>{member.displayName || '—'}</TableCell>
                                                        <TableCell>{member.mail || '—'}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            )}

                            {adGroupResult.exists && adGroupResult.members.length === 0 && (
                                <p className="text-sm text-muted-foreground">
                                    The group exists but has no members.
                                </p>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default ManageRequests
